import React, { useState } from 'react'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { Layout, SEO, Input } from '../../../shared/components'
import { useCart } from '../../cart/contexts/CartContext'
import { useBattery } from '../hooks'
import { useBatteryReviews } from '../../../hooks/useReviews'
import { ReviewSummary } from '../../../types/api'
import { buildImageUrl } from '../../../config'
import { getEffectiveUnitPrice } from '../../../constants/checkout'

const CSS_HANDLES = [
  'batteryDetails',
  'detailsContainer',
  'detailsContent',
  'detailsImages',
  'mainImage',
  'thumbnailImages',
  'thumbnailImage',
  'detailsInfo',
  'detailsHeader',
  'detailsTitle',
  'detailsBrand',
  'detailsModel',
  'detailsRating',
  'detailsStars',
  'detailsReviewCount',
  'detailsPrice',
  'detailsOriginalPrice',
  'detailsDiscount',
  'detailsStock',
  'detailsDescription',
  'detailsSpecs',
  'specsTable',
  'specRow',
  'specLabel',
  'specValue',
  'detailsActions',
  'quantitySelector',
  'quantityInput',
  'quantityButton',
  'addToCartButton',
  'buyNowButton',
  'detailsTabs',
  'tabButton',
  'tabContent',
  'tabPanel',
  'reviewsSection',
  'reviewItem',
  'reviewHeader',
  'reviewUser',
  'reviewRating',
  'reviewTitle',
  'reviewComment',
  'reviewDate',
  'breadcrumb',
  'breadcrumbItem',
  'breadcrumbLink'
] as const

const BatteryDetails: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { addItem, openCart } = useCart()

  // Usar hook customizado para buscar bateria
  const { battery, loading, error } = useBattery(undefined, slug)

  const {
    reviews,
    summary: reviewSummary,
    loading: reviewsLoading,
    error: reviewsError,
    setRating: setReviewRating,
    setSort: setReviewSort
  } = useBatteryReviews(battery?.id)

  const [selectedImage, setSelectedImage] = useState(0)
  const [quantity, setQuantity] = useState(1)
  const [willReturnScrap, setWillReturnScrap] = useState(true)
  const [activeTab, setActiveTab] = useState('description')
  const [imageError, setImageError] = useState(false)
  const [selectedRatingFilter, setSelectedRatingFilter] = useState<number | undefined>(undefined)
  const [selectedSort, setSelectedSort] = useState<'createdAt' | 'rating' | 'helpful'>('createdAt')
  const [selectedSortOrder, setSelectedSortOrder] = useState<'asc' | 'desc'>('desc')
  const sortLabels: Record<'createdAt' | 'rating' | 'helpful', string> = {
    createdAt: 'Mais recentes',
    rating: 'Avaliação',
    helpful: 'Mais úteis'
  }

  const renderSkeleton = () => (
    <Layout>
      <div className={`${handles.batteryDetails} min-h-screen bg-gray-50`}>
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
          <div className="flex items-center space-x-2">
            <div className="h-4 w-16 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
            <div className="h-4 w-4 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
            <div className="h-4 w-24 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
            <div className="h-4 w-4 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
            <div className="h-4 w-48 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="grid grid-cols-1 gap-8 p-8 lg:grid-cols-2">
              <div className="space-y-4">
                <div className="h-96 w-full rounded-lg bg-gray-200 animate-pulse" aria-hidden="true" />
                <div className="flex space-x-2">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div
                      key={`image-skeleton-${index}`}
                      className="h-20 w-20 rounded-lg bg-gray-200 animate-pulse"
                      aria-hidden="true"
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-3">
                  <div className="h-8 w-3/4 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                  <div className="h-4 w-1/2 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                  <div className="flex items-center space-x-3">
                    <div className="h-6 w-32 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                    <div className="h-4 w-20 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="h-10 w-48 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                  <div className="flex items-center space-x-4">
                    <div className="h-12 w-40 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                    <div className="h-8 w-20 rounded-full bg-gray-200 animate-pulse" aria-hidden="true" />
                  </div>
                </div>

                <div className="h-5 w-48 rounded bg-gray-200 animate-pulse" aria-hidden="true" />

                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="h-10 w-24 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                    <div className="h-10 flex-1 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="h-12 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                    <div className="h-12 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200">
              <div className="flex flex-wrap">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={`tab-skeleton-${index}`}
                    className="m-2 h-10 w-32 rounded bg-gray-200 animate-pulse"
                    aria-hidden="true"
                  />
                ))}
              </div>
              <div className="p-8">
                <div className="space-y-3">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div
                      key={`content-skeleton-${index}`}
                      className="h-4 w-full rounded bg-gray-200 animate-pulse"
                      aria-hidden="true"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )

  // Reset erro de imagem quando a bateria mudar
  React.useEffect(() => {
    setImageError(false)
  }, [battery?.id])

  React.useEffect(() => {
    setSelectedRatingFilter(undefined)
    setSelectedSort('createdAt')
    setSelectedSortOrder('desc')
    setReviewRating(undefined)
    setReviewSort('createdAt', 'desc')
  }, [battery?.id, setReviewRating, setReviewSort])

  // Ajustar quantidade quando a bateria mudar ou quando o estoque for menor que a quantidade selecionada
  React.useEffect(() => {
    if (battery) {
      if (battery.stock === 0) {
        setQuantity(1)
      } else if (quantity > battery.stock) {
        setQuantity(battery.stock)
      }
    }
  }, [battery?.id, battery?.stock])

  const handleImageError = () => {
    setImageError(true)
  }

  const handleRatingFilter = (rating?: number) => {
    const nextRating = selectedRatingFilter === rating ? undefined : rating
    setSelectedRatingFilter(nextRating)
    setReviewRating(nextRating)
  }

  const handleSortChange = (sortBy: 'createdAt' | 'rating' | 'helpful') => {
    let nextOrder: 'asc' | 'desc' = 'desc'

    if (selectedSort === sortBy) {
      nextOrder = selectedSortOrder === 'desc' ? 'asc' : 'desc'
    } else {
      nextOrder = sortBy === 'createdAt' ? 'desc' : 'desc'
    }

    setSelectedSort(sortBy)
    setSelectedSortOrder(nextOrder)
    setReviewSort(sortBy, nextOrder)
  }

  // Loading state
  if (loading) {
    return renderSkeleton()
  }

  // Error state
  if (error || !battery) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-gray-400 text-6xl mb-4">🔋</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Bateria não encontrada
            </h3>
            <p className="text-gray-600 mb-4">
              {error || 'A bateria que você está procurando não existe ou foi removida.'}
            </p>
            <Link
              to="/catalogo"
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Voltar ao Catálogo
            </Link>
          </div>
        </div>
      </Layout>
    )
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  const renderStars = (rating: number) => {
    const stars = []
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <svg
          key={i}
          className={`w-5 h-5 ${i <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300 fill-current'}`}
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      )
    }
    return stars
  }

  const formatReviewDate = (date: Date) => {
    try {
      return date.toLocaleDateString('pt-BR')
    } catch {
      return ''
    }
  }

  const formatDescription = (text: string) => {
    if (!text) return null

    // Dividir por quebras de linha
    const lines = text.split('\n')
    
    return lines.map((line, lineIndex) => {
      // Processar cada linha para destacar texto entre ** ou *
      const parts: (string | React.ReactElement)[] = []
      let currentIndex = 0
      
      // Encontrar todos os matches de **texto** e *texto*
      const matches: Array<{ start: number; end: number; content: string }> = []
      
      // Primeiro, processar **texto** (dois asteriscos)
      const doubleBoldRegex = /\*\*([^*]+?)\*\*/g
      let match
      const doubleMatches: Array<{ start: number; end: number }> = []
      while ((match = doubleBoldRegex.exec(line)) !== null) {
        const start = match.index
        const end = match.index + match[0].length
        matches.push({
          start,
          end,
          content: match[1]
        })
        doubleMatches.push({ start, end })
      }

      // Depois, processar *texto* (um asterisco) que não está dentro de **
      const singleBoldRegex = /\*([^*]+?)\*/g
      while ((match = singleBoldRegex.exec(line)) !== null) {
        const start = match.index
        const end = match.index + match[0].length
        
        // Verificar se não está dentro de um match de **
        const isInsideDouble = doubleMatches.some(
          r => start >= r.start && start < r.end
        )
        if (!isInsideDouble) {
          matches.push({
            start,
            end,
            content: match[1]
          })
        }
      }

      // Ordenar matches por posição
      matches.sort((a, b) => a.start - b.start)

      // Processar matches
      matches.forEach((matchItem, matchIndex) => {
        // Adicionar texto antes do match
        if (matchItem.start > currentIndex) {
          parts.push(line.substring(currentIndex, matchItem.start))
        }
        
        // Adicionar texto em negrito
        parts.push(
          <strong key={`bold-${lineIndex}-${matchIndex}`} className="font-semibold text-gray-900">
            {matchItem.content}
          </strong>
        )
        
        currentIndex = matchItem.end
      })
      
      // Adicionar texto restante
      if (currentIndex < line.length) {
        parts.push(line.substring(currentIndex))
      }
      
      // Se não houve matches, retornar a linha original
      if (parts.length === 0) {
        parts.push(line)
      }
      
      return (
        <React.Fragment key={`line-${lineIndex}`}>
          {parts}
          {lineIndex < lines.length - 1 && <br />}
        </React.Fragment>
      )
    })
  }

  const handleAddToCart = () => {
    if (!battery) return
    const scrap = battery.allowsScrapReturn ? willReturnScrap : false
    addItem(battery, quantity, true, scrap)
    openCart()
  }

  const handleBuyNow = () => {
    if (!battery) return
    const scrap = battery.allowsScrapReturn ? willReturnScrap : false
    addItem(battery, quantity, false, scrap)
    navigate('/checkout')
  }

  return (
    <Layout>
      <SEO
        title={battery.name}
        description={battery.description}
        image={battery.images && battery.images.length > 0 ? buildImageUrl(battery.images[0]) : undefined}
      />
      <div className={`${handles.batteryDetails} min-h-screen bg-gray-50`}>
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Breadcrumb */}
          <nav className={`${handles.breadcrumb} mb-6`}>
            <ol className="flex items-center space-x-2 text-sm text-gray-600">
              <li>
                <Link to="/" className={`${handles.breadcrumbLink} hover:text-blue-600`}>
                  Início
                </Link>
              </li>
              <li className={`${handles.breadcrumbItem}`}>/</li>
              <li>
                <Link to="/catalogo" className={`${handles.breadcrumbLink} hover:text-blue-600`}>
                  Baterias
                </Link>
              </li>
              <li className={`${handles.breadcrumbItem}`}>/</li>
              <li className={`${handles.breadcrumbItem} text-gray-900`}>
                {battery.name}
              </li>
            </ol>
          </nav>

          <div className={`${handles.detailsContainer} bg-white rounded-lg shadow-sm overflow-hidden`}>
            <div className={`${handles.detailsContent} grid grid-cols-1 lg:grid-cols-2 gap-8 p-8`}>
              {/* Imagens */}
              <div className={`${handles.detailsImages}`}>
                <div className={`${handles.mainImage} mb-4 bg-gray-50 rounded-lg overflow-hidden`}>
                  {!imageError && battery.images && battery.images.length > 0 ? (
                    <img
                      src={buildImageUrl(battery.images[selectedImage])}
                      alt={battery.name}
                      onError={handleImageError}
                      className="w-full h-96 object-contain"
                    />
                  ) : (
                    <div className="w-full h-96 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                      <svg
                        className="w-40 h-40 text-gray-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1}
                          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                {!imageError && battery.images && battery.images.length > 1 && (
                  <div className={`${handles.thumbnailImages} flex space-x-2`}>
                    {battery.images.map((image, index) => {
                      const imageUrl = buildImageUrl(image) as string
                      return (
                        <button
                          key={index}
                          onClick={() => setSelectedImage(index)}
                          className={`${handles.thumbnailImage} w-20 h-20 rounded-lg overflow-hidden border-2 ${selectedImage === index ? 'border-blue-600' : 'border-gray-200'
                            }`}
                        >
                          <img
                            src={imageUrl}
                            alt={`${battery.name} ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Informações */}
              <div className={`${handles.detailsInfo}`}>
                <div className={`${handles.detailsHeader} mb-6`}>
                  <h1 className={`${handles.detailsTitle} text-3xl font-bold text-gray-900 mb-2`}>
                    {battery.name}
                  </h1>
                  <div className={`${handles.detailsBrand} text-lg text-gray-600 mb-2`}>
                    {battery.brand ? `${battery.brand}${battery.model ? ` - ${battery.model}` : ''}` : battery.model || ''}
                  </div>

                  <div className={`${handles.detailsRating} flex items-center mb-4`}>
                    <div className={`${handles.detailsStars} flex mr-2`}>
                      {renderStars(battery.rating)}
                    </div>
                    <span className={`${handles.detailsReviewCount} text-sm text-gray-600`}>
                      ({battery.reviewCount} avaliações)
                    </span>
                  </div>

                  <div className={`${handles.detailsPrice} mb-4`}>
                    {battery.originalPrice && battery.originalPrice > battery.price && (
                      <div className={`${handles.detailsOriginalPrice} text-lg text-gray-500 line-through mb-1`}>
                        {formatPrice(battery.originalPrice)}
                      </div>
                    )}
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                      {battery.allowsScrapReturn && !willReturnScrap && (
                        <span className="text-lg text-gray-500 line-through">
                          {formatPrice(battery.price)}
                        </span>
                      )}
                      <span
                        key={battery.allowsScrapReturn ? (willReturnScrap ? 'with-scrap' : 'without-scrap') : 'fixed'}
                        className={`text-4xl font-bold text-blue-600 inline-block ${battery.allowsScrapReturn && !willReturnScrap ? 'animate-price-update' : ''}`}
                      >
                        {formatPrice(
                          battery.allowsScrapReturn
                            ? getEffectiveUnitPrice(
                                battery.price,
                                battery.weight ?? 0,
                                true,
                                willReturnScrap
                              )
                            : battery.price
                        )}
                      </span>
                      {battery.allowsScrapReturn && !willReturnScrap && (
                        <span className="text-sm text-amber-700 font-medium">
                          (com acréscimo sem sucata)
                        </span>
                      )}
                      {battery.discount && (
                        <span className={`${handles.detailsDiscount} bg-yellow-500 text-white text-sm px-3 py-1 rounded-full`}>
                          -{battery.discount}%
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={`${handles.detailsStock} mb-6`}>
                    {battery.stock > 0 ? (
                      <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3">
                        <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-green-800 font-semibold">
                          Em estoque
                        </span>
                        <span className="text-green-600 text-sm">
                          ({battery.stock} {battery.stock === 1 ? 'unidade disponível' : 'unidades disponíveis'})
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 rounded-lg bg-red-50 border-2 border-red-200 px-4 py-3">
                        <svg className="h-6 w-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-red-800 font-semibold text-base mb-1">
                            Produto Indisponível
                          </p>
                          <p className="text-red-600 text-sm">
                            Este produto está temporariamente fora de estoque.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Ações */}
                <div className={`${handles.detailsActions} mb-8`}>
                  {battery.allowsScrapReturn && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Entrega em Campinas/região? Escolha como quer a bateria:
                      </p>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setWillReturnScrap(true)}
                          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 transition-colors ${
                            willReturnScrap
                              ? 'border-green-500 bg-green-50 text-green-800'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-green-300 hover:bg-green-50/50'
                          }`}
                          aria-pressed={willReturnScrap}
                        >
                          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          <span className="text-sm font-medium">À base de troca (vou devolver a usada)</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setWillReturnScrap(false)}
                          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 transition-colors ${
                            !willReturnScrap
                              ? 'border-amber-500 bg-amber-50 text-amber-800'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-amber-300 hover:bg-amber-50/50'
                          }`}
                          aria-pressed={!willReturnScrap}
                        >
                          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <span className="text-sm font-medium">Sem sucata (acréscimo R$ 5,50/kg)</span>
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Válido para entrega ou retirada em Campinas e região (CEP 13xxx).
                      </p>
                    </div>
                  )}
                  <div className={`${handles.quantitySelector} flex items-center mb-4`}>
                    <span className="text-sm text-gray-600 mr-3">Quantidade:</span>
                    <div className="flex items-center border border-gray-300 rounded-lg">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        disabled={quantity <= 1}
                        className={`${handles.quantityButton} px-3 py-2 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        -
                      </button>
                      <Input
                        type="number"
                        value={quantity}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1
                          const maxQuantity = battery.stock > 0 ? battery.stock : 1
                          setQuantity(Math.max(1, Math.min(value, maxQuantity)))
                        }}
                        className={`${handles.quantityInput} w-16 px-2 py-2 text-center border-0`}
                        min="1"
                        max={battery.stock}
                      />
                      <button
                        onClick={() => setQuantity(Math.min(battery.stock, quantity + 1))}
                        disabled={quantity >= battery.stock || battery.stock === 0}
                        className={`${handles.quantityButton} px-3 py-2 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      onClick={handleAddToCart}
                      disabled={battery.stock === 0}
                      className={`${handles.addToCartButton} flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors`}
                    >
                      Adicionar ao Carrinho
                    </button>
                    <button
                      onClick={handleBuyNow}
                      disabled={battery.stock === 0}
                      className={`${handles.buyNowButton} flex-1 bg-yellow-500 text-gray-900 font-semibold py-3 px-6 rounded-lg hover:bg-yellow-600 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors`}
                    >
                      Comprar Agora
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className={`${handles.detailsTabs} border-t border-gray-200`}>
              <div className="flex">
                {[
                  { id: 'description', label: 'Descrição' },
                  { id: 'specifications', label: 'Especificações' },
                  { id: 'reviews', label: `Avaliações (${battery.reviewCount})` }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`${handles.tabButton} px-6 py-4 text-sm font-medium border-b-2 ${activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className={`${handles.tabContent} p-8`}>
                {/* Descrição */}
                {activeTab === 'description' && (
                  <div className={`${handles.tabPanel}`}>
                    <div className={`${handles.detailsDescription} text-gray-700 leading-relaxed`}>
                      {formatDescription(battery.description)}
                    </div>
                  </div>
                )}

                {/* Especificações */}
                {activeTab === 'specifications' && (
                  <div className={`${handles.tabPanel}`}>
                    <div className={`${handles.detailsSpecs}`}>
                      <table className={`${handles.specsTable} w-full`}>
                        <tbody>
                          {(() => {
                            // Função para normalizar nome da especificação para comparação
                            const normalizeSpecName = (name: string): string => {
                              return name.toLowerCase().trim().replace(/\s+/g, ' ')
                            }

                            // Remover duplicatas baseado no nome normalizado
                            const uniqueSpecs = battery.specifications.filter((spec, index, self) => {
                              const normalizedName = normalizeSpecName(spec.name)
                              return index === self.findIndex(s => normalizeSpecName(s.name) === normalizedName)
                            })

                            // Verificar se peso já existe nas especificações (usando o array único)
                            const hasWeightSpec = uniqueSpecs.some(
                              spec => spec.name.toLowerCase().includes('peso') || spec.name.toLowerCase().includes('weight')
                            )
                            
                            // Verificar se dimensões já existem nas especificações (usando o array único)
                            const hasLengthSpec = uniqueSpecs.some(
                              spec => spec.name.toLowerCase().includes('comprimento') || spec.name.toLowerCase().includes('length')
                            )
                            const hasWidthSpec = uniqueSpecs.some(
                              spec => spec.name.toLowerCase().includes('largura') || spec.name.toLowerCase().includes('width')
                            )
                            const hasHeightSpec = uniqueSpecs.some(
                              spec => spec.name.toLowerCase().includes('altura') || spec.name.toLowerCase().includes('height')
                            )

                            return (
                              <>
                                {/* Renderizar especificações únicas */}
                                {uniqueSpecs.map((spec, index) => (
                                  <tr key={index} className={`${handles.specRow} border-b border-gray-200`}>
                                    <td className={`${handles.specLabel} py-3 font-medium text-gray-900`}>
                                      {spec.name}:
                                    </td>
                                    <td className={`${handles.specValue} py-3 text-gray-700`}>
                                      {spec.value} {spec.unit && <span className="text-gray-500">{spec.unit}</span>}
                                    </td>
                                  </tr>
                                ))}
                                
                                {/* Adicionar peso se não existir nas especificações */}
                                {battery.weight > 0 && !hasWeightSpec && (
                                  <tr className={`${handles.specRow} border-b border-gray-200`}>
                                    <td className={`${handles.specLabel} py-3 font-medium text-gray-900`}>
                                      Peso:
                                    </td>
                                    <td className={`${handles.specValue} py-3 text-gray-700`}>
                                      {battery.weight.toFixed(3)} <span className="text-gray-500">kg</span>
                                    </td>
                                  </tr>
                                )}
                                
                                {/* Adicionar dimensões se não existirem nas especificações */}
                                {(battery.dimensions.length > 0 || battery.dimensions.width > 0 || battery.dimensions.height > 0) && (
                                  <>
                                    {battery.dimensions.length > 0 && !hasLengthSpec && (
                                      <tr className={`${handles.specRow} border-b border-gray-200`}>
                                        <td className={`${handles.specLabel} py-3 font-medium text-gray-900`}>
                                          Comprimento:
                                        </td>
                                        <td className={`${handles.specValue} py-3 text-gray-700`}>
                                          {battery.dimensions.length.toFixed(2)} <span className="text-gray-500">cm</span>
                                        </td>
                                      </tr>
                                    )}
                                    {battery.dimensions.width > 0 && !hasWidthSpec && (
                                      <tr className={`${handles.specRow} border-b border-gray-200`}>
                                        <td className={`${handles.specLabel} py-3 font-medium text-gray-900`}>
                                          Largura:
                                        </td>
                                        <td className={`${handles.specValue} py-3 text-gray-700`}>
                                          {battery.dimensions.width.toFixed(2)} <span className="text-gray-500">cm</span>
                                        </td>
                                      </tr>
                                    )}
                                    {battery.dimensions.height > 0 && !hasHeightSpec && (
                                      <tr className={`${handles.specRow} border-b border-gray-200`}>
                                        <td className={`${handles.specLabel} py-3 font-medium text-gray-900`}>
                                          Altura:
                                        </td>
                                        <td className={`${handles.specValue} py-3 text-gray-700`}>
                                          {battery.dimensions.height.toFixed(2)} <span className="text-gray-500">cm</span>
                                        </td>
                                      </tr>
                                    )}
                                  </>
                                )}
                              </>
                            )
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Reviews */}
                {activeTab === 'reviews' && (
                  <div className={`${handles.tabPanel}`}>
                    <div className={`${handles.reviewsSection} space-y-6`}>
                      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Avaliações dos clientes</h3>
                          <p className="text-sm text-gray-600">
                            {reviewSummary
                              ? `${reviewSummary.totalReviews} ${reviewSummary.totalReviews === 1 ? 'avaliação' : 'avaliações'}`
                              : 'Nenhuma avaliação encontrada'}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {(['createdAt', 'rating', 'helpful'] as const).map((sort) => {
                            const isSelected = selectedSort === sort

                            return (
                              <button
                                key={sort}
                                type="button"
                                onClick={() => handleSortChange(sort)}
                                className={`px-3 py-2 text-sm rounded-full border transition-colors ${isSelected
                                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                                  : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-800'
                                  }`}
                              >
                                <span>{sortLabels[sort]}</span>
                                {isSelected && (
                                  <span className="ml-1 text-xs" aria-hidden="true">
                                    {selectedSortOrder === 'desc' ? '↓' : '↑'}
                                  </span>
                                )}
                              </button>
                            )
                          })}
                        </div>
                      </div>

                      {reviewSummary && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                            <div>
                              <div className="text-4xl font-bold text-gray-900">
                                {reviewSummary.averageRating.toFixed(1)}
                              </div>
                              <div className="text-sm text-gray-600">
                                Média baseada em {reviewSummary.totalReviews}{' '}
                                {reviewSummary.totalReviews === 1 ? 'avaliação' : 'avaliações'}
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                              {[5, 4, 3, 2, 1].map((star) => {
                                const isSelected = selectedRatingFilter === star
                                const count = reviewSummary.ratingDistribution[star as keyof ReviewSummary['ratingDistribution']] ?? 0

                                return (
                                  <button
                                    key={star}
                                    type="button"
                                    onClick={() => handleRatingFilter(star)}
                                    className={`px-3 py-2 text-sm rounded-full border transition-colors ${isSelected
                                      ? 'border-yellow-500 bg-yellow-50 text-yellow-600'
                                      : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-800'
                                      }`}
                                  >
                                    <span className="font-medium">{star}★</span>
                                    <span className="ml-2 text-xs text-gray-500">{count}</span>
                                  </button>
                                )
                              })}
                              {selectedRatingFilter !== undefined && (
                                <button
                                  type="button"
                                  onClick={() => handleRatingFilter(undefined)}
                                  className="px-3 py-2 text-sm rounded-full border border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-800 transition-colors"
                                >
                                  Limpar filtro
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {reviewsLoading && (
                        <div className="space-y-6" role="status" aria-live="polite">
                          {Array.from({ length: 3 }).map((_, index) => (
                            <div
                              key={`review-skeleton-${index}`}
                              className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
                            >
                              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                                <div className="h-4 w-48 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                                <div className="h-4 w-32 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                              </div>
                              <div className="mb-3 h-4 w-24 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                              <div className="space-y-2">
                                {Array.from({ length: 3 }).map((_, lineIndex) => (
                                  <div
                                    key={`review-line-${index}-${lineIndex}`}
                                    className="h-3 w-full rounded bg-gray-200 animate-pulse"
                                    aria-hidden="true"
                                  />
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {!reviewsLoading && reviewsError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
                          {reviewsError}
                        </div>
                      )}

                      {!reviewsLoading && !reviewsError && reviews.length === 0 && (
                        <div className="bg-gray-50 border border-gray-200 text-gray-600 p-6 rounded-lg text-center">
                          Ainda não há avaliações para este produto.
                        </div>
                      )}

                      {!reviewsLoading && !reviewsError && reviews.length > 0 && (
                        <div className="space-y-6">
                          {reviews.map((review) => (
                            <div key={review.id} className={`${handles.reviewItem} border-b border-gray-200 pb-6`}>
                              <div className={`${handles.reviewHeader} mb-3`}>
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                  <div className={`${handles.reviewUser} font-medium text-gray-900`}>
                                    {review.userName}
                                    {review.verifiedPurchase && (
                                      <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                        Compra verificada
                                      </span>
                                    )}
                                  </div>
                                  <div className={`${handles.reviewDate} text-sm text-gray-500`}>
                                    {formatReviewDate(review.createdAt)}
                                  </div>
                                </div>
                                <div className={`${handles.reviewRating} flex items-center mt-2`}>
                                  <div className="flex mr-2">
                                    {renderStars(review.rating)}
                                  </div>
                                  {review.helpful > 0 && (
                                    <span className="text-sm text-gray-600">
                                      {review.helpful} {review.helpful === 1 ? 'pessoa achou' : 'pessoas acharam'} útil
                                    </span>
                                  )}
                                </div>
                              </div>
                              <h4 className={`${handles.reviewTitle} font-medium text-gray-900 mb-2`}>
                                {review.title}
                              </h4>
                              <p className={`${handles.reviewComment} text-gray-700`}>
                                {review.comment}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default BatteryDetails
